/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpParams,
  HttpParameterCodec,
} from '@angular/common/http';
import { throwError, Observable, BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { UserService } from '../user.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<unknown> =
    new BehaviorSubject<unknown>(null);

  constructor(private userService: UserService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Ignore requests when user is not logged on
    if (!this.userService.isLoggedOn()) {
      // But still encode properly
      const params = new HttpParams({
        encoder: new CustomEncoder(),
        fromString: request.params.toString(),
      });
      request = request.clone({
        setHeaders: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: 'Thu, 01 Jan 1970 00:00:00 GMT',
          'If-Modified-Since': '0',
        },
        params,
        setParams: {
          Nonce: Date.now().toFixed(),
        },
      });
      return next.handle(request);
    }

    // Ignore requests with X-Skip-Interceptor header
    if (request.headers.has('X-Skip-Interceptor')) {
      const headers = request.headers.delete('X-Skip-Interceptor');
      return next.handle(request.clone({ headers }));
    }

    // Add access token and user id to all requests
    request = this.addHeaders(request, this.userService.getAccessToken());

    // Send request and check for unauthorized
    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          // Got unauthorized, refresh and retry
          return this.refreshAndRetry(request, next);
        } else {
          // Not 401 just throw error
          return throwError(error);
        }
      }),
    );
  }

  private addHeaders(request: HttpRequest<unknown>, token: string) {
    const params = new HttpParams({
      encoder: new CustomEncoder(),
      fromString: request.params.toString(),
    });
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        UserId: this.userService.user?.id || '',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: 'Thu, 01 Jan 1970 00:00:00 GMT',
        'If-Modified-Since': '0',
      },
      params,
      setParams: {
        Nonce: Date.now().toFixed(),
      },
    });
  }

  private refreshAndRetry(request: HttpRequest<unknown>, next: HttpHandler) {
    // If already refreshing, put requests in a queue until we have a valid access token
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((accessToken) => {
          return next.handle(this.addHeaders(request, accessToken.toString()));
        }),
      );
    } else {
      // Put a hold on future requests
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      //Refresh token
      return this.userService.refreshTokenObs().pipe(
        catchError((error) => {
          this.userService.logOff();
          this.isRefreshing = false;
          console.error('Error refreshing token:', error);
          return EMPTY;
        }),
        switchMap((userObj) => {
          // Update user with new values
          this.userService.setUserAndNotify(userObj, true);
          // Allow queued requests to complete with new access token
          this.isRefreshing = false;
          this.refreshTokenSubject.next(this.userService.getAccessToken());

          // Resend Request
          return next.handle(
            this.addHeaders(request, this.userService.getAccessToken()),
          );
        }),
      );
    }
  }
}

class CustomEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}
